import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Select, Button, Typography, Spin, Row, Col, Empty, Tag } from 'antd';
import { TrophyOutlined, GiftOutlined, CrownOutlined, LoginOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { driverService } from '../../common/services/api';
import './DriverPage.css';

const { Title, Paragraph } = Typography;

const DriverRankingPage = () => {
    const [loading, setLoading] = useState(true);
    const [rankings, setRankings] = useState([]);
    const [myRanking, setMyRanking] = useState(null);
    const [period, setPeriod] = useState(null);
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [driverUser, setDriverUser] = useState(null);
    const [driverCity, setDriverCity] = useState(null);

    // Charger les villes disponibles (pour les visiteurs non connectés)
    const loadCities = useCallback(async () => {
        try {
            const response = await driverService.getCities();
            if (response.success) {
                setCities(response.data.cities || []);
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('driverToken');
        const userData = localStorage.getItem('driverUser');
        setIsLoggedIn(!!token);

        let cityId = null;
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setDriverUser(parsedUser);
                // Récupérer la ville du chauffeur connecté
                if (parsedUser.city) {
                    setDriverCity(parsedUser.city);
                    cityId = parsedUser.city._id;
                }
            } catch (e) {
                console.error('Erreur parsing user data:', e);
            }
        }

        // Charger les villes (pour les non-connectés)
        if (!token) {
            loadCities();
        }

        // Charger le classement (filtré par ville si connecté)
        loadRanking(null, cityId);
    }, [loadCities]);

    const loadRanking = async (selectedPeriod = null, cityId = null) => {
        setLoading(true);
        try {
            // Charger le classement (filtré par la ville si spécifié)
            const rankingResponse = await driverService.getRanking(selectedPeriod, cityId);
            if (rankingResponse.success) {
                setRankings(rankingResponse.data.rankings || []);
                setPeriod(rankingResponse.data.period);
                setAvailablePeriods(rankingResponse.data.availablePeriods || []);
            }

            // Si connecté, charger ma position
            const token = localStorage.getItem('driverToken');
            if (token) {
                try {
                    const myRankingResponse = await driverService.getMyRanking(token, selectedPeriod, cityId);
                    if (myRankingResponse.success && myRankingResponse.data.myRanking) {
                        setMyRanking(myRankingResponse.data);
                    } else {
                        setMyRanking(null);
                    }
                } catch (err) {
                    console.error('Erreur chargement ma position:', err);
                    setMyRanking(null);
                }
            }
        } catch (error) {
            console.error('Erreur chargement classement:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (value) => {
        // Recharger avec la ville appropriée
        const cityId = isLoggedIn ? (driverCity?._id || null) : selectedCity;
        loadRanking(value, cityId);
    };

    const handleCityChange = (value) => {
        setSelectedCity(value);
        loadRanking(period, value);
    };

    const formatPeriod = (p) => {
        if (!p) return '';
        const [year, month] = p.split('-');
        const months = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
                       'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
        return `${months[parseInt(month) - 1]} ${year}`;
    };

    const formatCurrency = (amount) => {
        // Masquer partiellement le montant (ex: 497 600 → 4** ***)
        const formatted = new Intl.NumberFormat('fr-FR').format(amount);
        let firstDigitFound = false;
        const masked = formatted.split('').map(char => {
            if (/\d/.test(char)) {
                if (!firstDigitFound) {
                    firstDigitFound = true;
                    return char; // Garde le premier chiffre
                }
                return '*'; // Masque les autres chiffres
            }
            return char; // Garde les espaces et séparateurs
        }).join('');
        return masked + ' FCFA';
    };

    const formatPhone = (phone) => {
        if (!phone) return '';
        // Enlever le +225 si présent
        let cleaned = phone.replace('+225', '').replace(/\D/g, '');
        // Formater en XX XX XX XX XX
        if (cleaned.length === 10) {
            return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
        }
        return cleaned;
    };

    const getRankBadgeClass = (rank) => {
        if (rank === 1) return 'gold';
        if (rank === 2) return 'silver';
        if (rank === 3) return 'bronze';
        return 'normal';
    };

    const handleLogout = () => {
        localStorage.removeItem('driverToken');
        localStorage.removeItem('driverUser');
        setIsLoggedIn(false);
        setMyRanking(null);
        setDriverUser(null);
        setDriverCity(null);
        setSelectedCity(null);
        // Charger les villes et le classement global
        loadCities();
        loadRanking(period, null);
    };

    // Obtenir le nom de la ville affichée
    const getDisplayedCityName = () => {
        if (isLoggedIn && driverCity) {
            return driverCity.name;
        }
        if (selectedCity) {
            const city = cities.find(c => c._id === selectedCity);
            return city?.name || null;
        }
        return null;
    };

    const displayedCityName = getDisplayedCityName();

    return (
        <div className="driver-ranking-page">
            {/* Hero Section */}
            <div className="ranking-hero">
                <Title level={1}>
                    <TrophyOutlined style={{ marginRight: 12 }} />
                    Classement des Chauffeurs
                </Title>
                <Paragraph className="subtitle">
                    Top 15 mensuel - Revenus des courses
                </Paragraph>
            </div>

            <div className="ranking-container">
                {/* Login Prompt or User Bar */}
                {!isLoggedIn ? (
                    <div className="login-prompt">
                        <Paragraph>
                            Connectez-vous pour voir le classement de votre ville
                        </Paragraph>
                        <Link to="/chauffeurs/connexion">
                            <Button type="primary" icon={<LoginOutlined />}>
                                Se connecter
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="user-bar">
                        <div className="user-bar-info">
                            <CrownOutlined className="user-bar-icon" />
                            <div className="user-bar-details">
                                <span className="user-bar-name">
                                    {driverUser?.firstName || 'Chauffeur'} {driverUser?.lastName || ''}
                                </span>
                                <span className="user-bar-phone">
                                    {formatPhone(driverUser?.phone)}
                                </span>
                            </div>
                            {myRanking?.myRanking && (
                                <span className="user-bar-rank">
                                    #{myRanking.myRanking.rank} - {formatCurrency(myRanking.myRanking.totalRevenue)}
                                </span>
                            )}
                        </div>
                        <Button
                            type="link"
                            onClick={handleLogout}
                            className="user-bar-logout"
                        >
                            Deconnexion
                        </Button>
                    </div>
                )}

                {/* Filters: Period & City Selector (city only for non-logged in users) */}
                <div className="ranking-filters">
                    <div className="filter-group">
                        <Select
                            value={period}
                            onChange={handlePeriodChange}
                            style={{ minWidth: 200 }}
                            size="large"
                            placeholder="Selectionner une periode"
                            labelRender={(props) => formatPeriod(props.value)}
                        >
                            {/* Si pas de périodes disponibles, afficher la période actuelle */}
                            {availablePeriods.length === 0 && period && (
                                <Select.Option key={period} value={period}>
                                    {formatPeriod(period)}
                                </Select.Option>
                            )}
                            {availablePeriods.map(p => (
                                <Select.Option key={p} value={p}>
                                    {formatPeriod(p)}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    {/* Sélecteur de ville uniquement pour les visiteurs non connectés */}
                    {!isLoggedIn && (
                        <div className="filter-group">
                            <Select
                                value={selectedCity}
                                onChange={handleCityChange}
                                style={{ minWidth: 180 }}
                                size="large"
                                placeholder="Toutes les villes"
                                allowClear
                                suffixIcon={<EnvironmentOutlined />}
                            >
                                {cities.map(city => (
                                    <Select.Option key={city._id} value={city._id}>
                                        {city.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>

                {/* Top 15 List */}
                <Card
                    className="top15-card"
                    title={
                        <span>
                            <TrophyOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                            Top 15 - {formatPeriod(period)}
                            {displayedCityName && (
                                <Tag color="blue" style={{ marginLeft: 12 }}>
                                    <EnvironmentOutlined /> {displayedCityName}
                                </Tag>
                            )}
                        </span>
                    }
                >
                    {loading ? (
                        <div className="loading-container">
                            <Spin size="large" />
                            <Paragraph style={{ marginTop: 16, color: '#999' }}>
                                Chargement du classement...
                            </Paragraph>
                        </div>
                    ) : rankings.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                displayedCityName
                                    ? `Aucun classement disponible pour ${displayedCityName} cette periode`
                                    : "Aucun classement disponible pour cette periode"
                            }
                        />
                    ) : (
                        <ul className="ranking-list">
                            {rankings.map((driver, index) => (
                                <li
                                    key={index}
                                    className={`ranking-item ${index < 3 ? 'top-3' : ''} ${driver.isMe ? 'is-me' : ''}`}
                                >
                                    <div className={`rank-badge ${getRankBadgeClass(driver.rank)}`}>
                                        {driver.rank}
                                    </div>
                                    <div className="driver-info">
                                        <div className="driver-name">
                                            {driver.firstName} {driver.lastName}
                                        </div>
                                        <div className="driver-phone">{driver.phone}</div>
                                    </div>
                                    <div className="driver-stats">
                                        <div className="driver-revenue">
                                            {formatCurrency(driver.totalRevenue)}
                                        </div>
                                        <div className="driver-orders">
                                            {driver.totalOrders} courses
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* Rewards Info */}
                <div className="rewards-info">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Card className="rewards-card">
                                <GiftOutlined className="reward-icon" />
                                <Title level={4}>Recompenses Mensuelles</Title>
                                <Paragraph>
                                    Les 15 premiers du classement mensuel sont recompenses chaque mois
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card className="rewards-card">
                                <TrophyOutlined className="reward-icon" />
                                <Title level={4}>Recompenses Trimestrielles</Title>
                                <Paragraph>
                                    Les 5 premiers du trimestre recoivent des recompenses exceptionnelles
                                </Paragraph>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default DriverRankingPage;
